import cx from 'classnames';
import {useCallback} from 'react';
import {useSelector} from 'react-redux';
import urls from 'urls';
import {apiClient} from '~/api/api-client';
import {userHasSubscriptionPlan} from '~/common/fabric/User/reducers/user';
import {Divider} from '~library/atoms/Divider';
import {useTracking} from '~library/context/tracking/tracking-context';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {userLoggedIn} from '~library/organisms/LoginModal/reducer';
import {AccountLineIcon} from '~library/svg/AccountLineIcon';
import {ActivityFeedLineIcon} from '~library/svg/ActivityFeedLineIcon';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {EditLineIcon} from '~library/svg/EditLineIcon';
import {FavoritesLineIcon} from '~library/svg/FavoritesLineIcon';
import {GuestListLineIcon} from '~library/svg/GuestListLineIcon';
import {LockLineIcon} from '~library/svg/LockLineIcon';
import {SettingsLineIcon} from '~library/svg/SettingsLineIcon';
import {LogoutIcon} from '~library/svg/v2/LogoutIcon';
import {USER_DATA_STORAGE_KEY} from '~library/utils/auth/utils';
import {coin} from '~libSass/base/_exports.sass';
import * as styles from './AccountDetailsDrawer.module.scss';
import {AcctDetailsProCTA} from './components/AccountDetailsProCTA/AccountDetailsProCTA';
import {AcctDrawerItem} from './components/AcctDrawerItem/AcctDrawerItem';
import {UserInfo} from './components/UserInfo';

type AccountDetailsDrawerProps = {
  open: boolean;
  onClose?: () => void;
  mobileAccountSettingsOpen?: boolean;
  onOpenMobileAccountSettings?: () => void;
  onCloseMobileAccountSettings?: () => void;
};

export const AccountDetailsDrawer = ({
  open,
  mobileAccountSettingsOpen = false,
  onOpenMobileAccountSettings,
}: AccountDetailsDrawerProps) => {
  const isLargishAndUp = useIsLargishAndUp();
  const hasSubscriptionPlan = useSelector(userHasSubscriptionPlan);
  const {track} = useTracking();

  const onLogoutHandler = useCallback(async () => {
    track({
      dataQaId: 'signOutAcctDrawerItem',
    });
    // @ts-ignore
    window.tags.page_event('signout_button');
    try {
      await apiClient.post(urls.get('ajax_logout'), undefined);
      // @ts-ignore
      userLoggedIn({});
    } catch (error) {
      evite.error('Error while posting to ajax_logout', error);
    }
    localStorage.removeItem(USER_DATA_STORAGE_KEY);
    return window.location.assign(urls.get('home'));
  }, [track]);

  return (
    <div
      className={cx(styles.accountDetailsDrawer, {
        [styles.open]: open,
      })}
      id="acctDetails"
    >
      <div className={styles.drawerCtaGroup}>
        {!mobileAccountSettingsOpen ? (
          <>
            <UserInfo />

            {isLargishAndUp && <Divider />}

            {!isLargishAndUp && !hasSubscriptionPlan && <AcctDetailsProCTA />}
            {!isLargishAndUp && hasSubscriptionPlan && <Divider />}

            <AcctDrawerItem
              href="/my-events"
              text="My Invitations"
              tabIndex={open ? 0 : -1}
              dataQaId="myEventsAcctDrawerItem"
            />
            <AcctDrawerItem
              href="/my-events?type=signup-sheets"
              text="My SignUp Sheets"
              tabIndex={open ? 0 : -1}
              dataQaId="mySignUpSheetsAcctDrawerItem"
            />
            <AcctDrawerItem
              href="/my-events?type=card"
              text="My Greeting Cards"
              tabIndex={open ? 0 : -1}
              dataQaId="myECardsAcctDrawerItem"
            />

            <Divider />

            <AcctDrawerItem
              href="/gallery/favorites"
              text={`My favorites${
                window.favoritesNumber !== undefined ? ` (${window.favoritesNumber})` : ''
              }`}
              icon={<FavoritesLineIcon ratio={0.625} color={coin} />}
              tabIndex={open ? 0 : -1}
              dataQaId="myFavoritesAcctDrawerItem"
            />

            <AcctDrawerItem
              href="/profile/contacts"
              text="Address book"
              icon={<GuestListLineIcon ratio={0.625} color={coin} />}
              tabIndex={open ? 0 : -1}
              dataQaId="addressBookAcctDrawerItem"
            />

            <AcctDrawerItem
              icon={<SettingsLineIcon ratio={0.625} color={coin} />}
              text="Account settings"
              onClick={isLargishAndUp ? undefined : onOpenMobileAccountSettings}
              href={isLargishAndUp ? '/settings/account' : undefined}
              type={isLargishAndUp ? 'link' : 'button'}
              tabIndex={open ? 0 : -1}
              dataQaId="accountSettingsAcctDrawerItem"
            />

            <AcctDrawerItem
              icon={<LogoutIcon ratio={0.625} color={coin} />}
              text="Sign out"
              onClick={onLogoutHandler}
              type="button"
              tabIndex={open ? 0 : -1}
              dataQaId="signOutAcctDrawerItem"
            />

            {isLargishAndUp && !hasSubscriptionPlan && <AcctDetailsProCTA />}
          </>
        ) : (
          <div className={styles.accountSettingsWrapper}>
            <AcctDrawerItem
              href="/profile/edit"
              text="Edit profile"
              icon={<EditLineIcon ratio={0.625} color={coin} />}
              tabIndex={open ? 0 : -1}
              dataQaId="editProfileAcctDrawerItem"
            />
            <AcctDrawerItem
              href="/profile"
              text="Account info"
              icon={<AccountLineIcon ratio={0.625} color={coin} />}
              tabIndex={open ? 0 : -1}
              dataQaId="accountInfoAcctDrawerItem"
            />
            <AcctDrawerItem
              href="/settings/blocks"
              text="Manage block list"
              icon={<AlertLineIcon ratio={0.625} color={coin} />}
              tabIndex={open ? 0 : -1}
              dataQaId="manageBlockListAcctDrawerItem"
            />
            <AcctDrawerItem
              href="/settings/data_privacy"
              text="Data privacy"
              icon={<LockLineIcon ratio={0.625} color={coin} />}
              tabIndex={open ? 0 : -1}
              dataQaId="dataPrivacyAcctDrawerItem"
            />
            <AcctDrawerItem
              href="/settings/pm_order_history"
              text="Your orders"
              icon={<ActivityFeedLineIcon ratio={0.625} color={coin} />}
              tabIndex={open ? 0 : -1}
              dataQaId="yourOrdersAcctDrawerItem"
            />
          </div>
        )}
      </div>
    </div>
  );
};
